import {
	CheckCircle2,
	ChevronRight,
	Circle,
	Loader2,
	MinusCircle,
	TriangleAlert,
	XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { badgeBase, statusClasses } from "@/lib/colors";
import { cn } from "@/lib/utils";
import {
	ADD_STEPS,
	describeCause,
	FIX_STEPS,
	formatDateTime,
	HOST_SIDE_STEPS,
	OPERATION_STATUS_LABELS,
	OPERATION_STATUS_VARIANT,
	okStepNote,
	operationKindLabel,
	operationWarnings,
	REMOVE_STEPS,
	SET_DEFAULT_STEPS,
	STEP_STATUS_LABELS,
	STEP_WARNING_CODES,
	singleDeviceOperation,
	stepLabel,
} from "./printer-labels";
import type { OperationStep, OperationStepStatus, PrinterOperation } from "./printers-api";
import {
	formatStepDuration,
	formatTotalDuration,
	operationDuration,
	operationTimeline,
	type WaterfallBar,
	waterfallBars,
} from "./step-timeline";

const STEP_ICON: Record<OperationStepStatus, typeof Circle> = {
	pending: Circle,
	running: Loader2,
	ok: CheckCircle2,
	failed: XCircle,
	skipped: MinusCircle,
};

const STEP_ICON_CLASS: Record<OperationStepStatus, string> = {
	pending: "text-muted-foreground",
	running: "motion-safe:animate-spin text-status-info-text",
	ok: "text-status-success-text",
	failed: "text-status-error-text",
	skipped: "text-muted-foreground",
};

const BAR_CLASS: Record<OperationStepStatus, string> = {
	pending: "border border-dashed border-muted-foreground bg-transparent",
	running: "bg-status-info-bg border border-status-info-border",
	ok: "bg-status-success-border",
	failed: "bg-status-error-text",
	skipped: "bg-border",
};

type StepGroupData = { title: string; steps: OperationStep[] };

/**
 * Grupy kroków: operacje na jednym komputerze mają jedną grupę, udostępnienie
 * i pochodne — „komputer z drukarką” (A) i „komputer docelowy” (B).
 */
export function operationStepGroups(operation: PrinterOperation): StepGroupData[] {
	const single = singleDeviceOperation(operation.kind);
	const singleStep = (s: OperationStep) =>
		REMOVE_STEPS.has(s.step) ||
		ADD_STEPS.has(s.step) ||
		FIX_STEPS.has(s.step) ||
		SET_DEFAULT_STEPS.has(s.step);
	const deviceSteps = single ? operation.steps.filter(singleStep) : [];
	const otherSteps = single ? operation.steps.filter((s) => !singleStep(s)) : operation.steps;
	const device = single ? singleDeviceName(operation) : null;
	return [
		{ title: `Komputer${device ? ` · ${device}` : ""}`, steps: deviceSteps },
		{
			title: `Komputer z drukarką${operation.hostDisplayName ? ` · ${operation.hostDisplayName}` : ""}`,
			steps: otherSteps.filter((s) => HOST_SIDE_STEPS.has(s.step)),
		},
		{
			title: `Komputer docelowy${operation.clientDisplayName ? ` · ${operation.clientDisplayName}` : ""}`,
			steps: otherSteps.filter((s) => !HOST_SIDE_STEPS.has(s.step)),
		},
	].filter((g) => g.steps.length > 0);
}

/** Pozycja i szerokość paska w % osi; czekające dostają miejsce za bieżącą chwilą. */
function barGeometry(
	bar: WaterfallBar,
	span: number,
	timed: boolean,
	index: number,
	total: number,
	waitingIndex: number,
): { left: number; width: number } {
	const MIN = 2;
	if (!timed) {
		// Bez czasów (historia): kolejność kroków, równe odcinki — czas pokazuje tylko nagłówek.
		const width = 100 / Math.max(1, total);
		return { left: index * width, width: Math.max(MIN, width - 1) };
	}
	if (bar.from == null) return { left: 0, width: MIN };
	if (bar.to == null) {
		// Czeka: krótki przerywany odcinek za ostatnim znanym końcem.
		const left = Math.min(96, (bar.from / span) * 100 + waitingIndex * 6);
		return { left, width: 5 };
	}
	const left = Math.min(100 - MIN, (bar.from / span) * 100);
	const width = Math.max(MIN, ((bar.to - bar.from) / span) * 100);
	return { left, width: Math.min(width, 100 - left) };
}

function StepTrack({ bar, left, width }: { bar: WaterfallBar; left: number; width: number }) {
	return (
		<div className="relative h-3 rounded-sm bg-muted" aria-hidden>
			<div
				className={cn(
					"absolute top-0 bottom-0 overflow-hidden rounded-sm motion-safe:transition-[left,width,background-color] motion-safe:duration-500",
					BAR_CLASS[bar.status],
				)}
				style={{ left: `${left}%`, width: `${width}%` }}
			>
				{bar.status === "running" ? (
					<svg className="absolute inset-0 size-full" preserveAspectRatio="none">
						<title>trwa</title>
						<line
							x1="0"
							y1="50%"
							x2="100%"
							y2="50%"
							strokeWidth="12"
							strokeDasharray="4 4"
							className="stroke-status-info-text motion-safe:animate-dash-flow"
						/>
					</svg>
				) : null}
			</div>
		</div>
	);
}

function StepRow({
	step,
	bar,
	geometry,
}: {
	step: OperationStep;
	bar: WaterfallBar;
	geometry: { left: number; width: number };
}) {
	const Icon = STEP_ICON[step.status] ?? Circle;
	// `kept`: krok przeszedł, ale niczego nie zmieniał; `installed`/`purged`/`partial`: co zrobił.
	const okNote = step.status === "ok" ? okStepNote(step.step, step.code) : null;
	const time =
		step.status === "skipped"
			? "—"
			: bar.duration != null
				? formatStepDuration(bar.duration)
				: step.status === "running"
					? "trwa"
					: bar.sameAck
						? "razem"
						: "—";
	return (
		<li
			className="grid grid-cols-[minmax(0,13rem)_minmax(0,1fr)_3.75rem] items-center gap-x-2 py-0.5 text-xs max-md:grid-cols-[minmax(0,9rem)_minmax(0,1fr)_3.25rem]"
			data-step={step.step}
			data-status={step.status}
		>
			<span className="flex min-w-0 items-center gap-1.5">
				<Icon className={cn("size-3.5 shrink-0", STEP_ICON_CLASS[step.status])} aria-hidden />
				<span
					className={cn(
						"truncate",
						step.status === "pending" && "text-muted-foreground",
						step.status === "running" && "font-medium",
					)}
					title={stepLabel(step.step)}
				>
					{stepLabel(step.step)}
				</span>
				<span className="sr-only"> — {STEP_STATUS_LABELS[step.status] ?? step.status}</span>
			</span>
			<StepTrack bar={bar} left={geometry.left} width={geometry.width} />
			<span
				className="text-right font-mono text-[11px] tabular-nums text-muted-foreground"
				title={
					bar.sameAck
						? "Komputer zgłosił ten krok razem z poprzednim — osobnego czasu nie znamy."
						: undefined
				}
			>
				{time}
			</span>
			{okNote || step.status === "skipped" || (step.status === "failed" && step.detail) ? (
				<span className="col-span-3 pl-5 text-[11px]">
					{step.status === "skipped" ? (
						<span className="text-muted-foreground">pominięte</span>
					) : null}
					{okNote ? <span className="text-muted-foreground">{okNote}</span> : null}
					{step.status === "failed" && step.detail ? (
						<span className="text-status-error-text">{step.detail}</span>
					) : null}
				</span>
			) : null}
		</li>
	);
}

function formatWin32(code: number): string {
	return `${code} (0x${(code >>> 0).toString(16).toUpperCase()})`;
}

function TechnicalDetails({ operation }: { operation: PrinterOperation }) {
	const [open, setOpen] = useState(false);
	const withInfo = operation.steps.filter(
		(s) => s.code != null || s.win32 != null || s.detail != null,
	);
	if (withInfo.length === 0 && !operation.cause && !operation.errorDetail) return null;
	return (
		<Collapsible open={open} onOpenChange={setOpen}>
			<CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
				<ChevronRight
					className={cn("size-3.5 motion-safe:transition-transform", open && "rotate-90")}
				/>
				Szczegóły techniczne
			</CollapsibleTrigger>
			<CollapsibleContent>
				<dl className="mt-1 space-y-1 rounded-md border bg-muted/40 p-2 font-mono text-xs">
					<div>
						<dt className="inline text-muted-foreground">operacja: </dt>
						<dd className="inline">#{operation.operationId}</dd>
					</div>
					{operation.cause ? (
						<div>
							<dt className="inline text-muted-foreground">przyczyna: </dt>
							<dd className="inline">{operation.cause}</dd>
						</div>
					) : null}
					{operation.errorDetail ? (
						<div>
							<dt className="inline text-muted-foreground">opis: </dt>
							<dd className="inline break-words">{operation.errorDetail}</dd>
						</div>
					) : null}
					{withInfo.map((s) => {
						const warning =
							s.status !== "failed" && s.code ? STEP_WARNING_CODES[s.code] : undefined;
						return (
							<div key={s.step}>
								<dt className="inline text-muted-foreground">{s.step}: </dt>
								<dd className="inline break-words">
									{[s.code, s.win32 != null ? `win32 ${formatWin32(s.win32)}` : null, s.detail]
										.filter(Boolean)
										.join(" · ")}
									{warning ? (
										<span className="block font-sans text-muted-foreground">
											Ostrzeżenie (krok udany): {warning}
										</span>
									) : null}
								</dd>
							</div>
						);
					})}
				</dl>
			</CollapsibleContent>
		</Collapsible>
	);
}

function successMessage(operation: PrinterOperation): string {
	const printer = operation.printerName ?? "Drukarka";
	const client = operation.clientDisplayName ?? "stanowisko";
	switch (operation.kind) {
		case "share_connect":
			return `Gotowe — ${client} drukuje na „${printer}”.`;
		case "repair":
			return `Gotowe — ${client} znów drukuje na „${printer}”.`;
		case "disconnect":
			return `Gotowe — ${client} nie drukuje już na „${printer}”.`;
		case "forget":
			return `Połączenie zapomniane. Jeśli ${client} wróci online, drukarkę na nim usuń ręcznie.`;
		case "unshare":
			return `Gotowe — „${printer}” nie jest już udostępniana.`;
		case "remove_printer":
		case "remove_connection": {
			const what = operation.kind === "remove_printer" ? "drukarka" : "połączenie";
			const verb = operation.kind === "remove_printer" ? "zniknęła" : "zniknęło";
			const device = singleDeviceName(operation);
			return `Gotowe — ${what} „${printer}” ${verb} z komputera${device ? ` ${device}` : ""}.`;
		}
		case "add_printer": {
			const device = singleDeviceName(operation);
			return `Gotowe — drukarka „${printer}” jest na komputerze${device ? ` ${device}` : ""}. Lista drukarek odświeży się za kilka sekund.`;
		}
		case "fix_printer": {
			const done = (step: string) => operation.steps.find((s) => s.step === step);
			const purge = done("fix_purge");
			const resume = done("fix_resume");
			const did = [
				purge?.status === "ok" && purge.code === "purged" ? "wyczyszczona" : null,
				resume?.status === "ok" && resume.code !== "kept" ? "wznowiona" : null,
			].filter(Boolean);
			return did.length > 0
				? `Gotowe — kolejka „${printer}” ${did.join(" i ")}. Stan drukarki odświeży się za kilka sekund.`
				: `Gotowe — kolejka „${printer}” nie wymagała zmian.`;
		}
		case "reset_spooler": {
			const device = singleDeviceName(operation);
			return `Gotowe — Bufor wydruku${device ? ` na komputerze ${device}` : ""} uruchomiony ponownie.`;
		}
		case "reinstall_printer": {
			const device = singleDeviceName(operation);
			return `Gotowe — drukarka „${printer}” zainstalowana ponownie${device ? ` na komputerze ${device}` : ""}. Lista drukarek odświeży się za kilka sekund.`;
		}
		case "set_default": {
			const device = singleDeviceName(operation);
			return `Gotowe — „${printer}” jest drukarką domyślną${device ? ` na komputerze ${device}` : ""}.`;
		}
		default:
			return "Gotowe.";
	}
}

/**
 * Komputer operacji na jednym komputerze: kolejka (usuwana, dodawana,
 * naprawiana) i bufor wydruku leżą na komputerze z drukarką (`host`),
 * połączenie na stanowisku (`client`).
 */
function singleDeviceName(operation: PrinterOperation): string | null {
	return operation.kind === "remove_connection"
		? operation.clientDisplayName
		: operation.hostDisplayName;
}

/** Bieżąca chwila co sekundę, tylko gdy operacja trwa (w spoczynku nic się nie odmalowuje). */
function useNow(active: boolean): number {
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		if (!active) return;
		setNow(Date.now());
		const timer = setInterval(() => setNow(Date.now()), 1_000);
		return () => clearInterval(timer);
	}, [active]);
	return now;
}

/**
 * Wodospad wykonania operacji: oś czasu kroków pogrupowanych na komputer z
 * drukarką i komputer docelowy (albo jeden komputer), czasy kroków widziane
 * przez panel, trwający krok wyróżniony ruchem, czekające przerywane, nieudany
 * z przyczyną po polsku. Wspólny dla planu, okna postępu i historii (tylko
 * odczyt — komponent nie ma akcji). Bez obserwacji na żywo (historia) kroki
 * stoją w kolejności, a czas ma tylko cała operacja.
 */
export function OperationWaterfall({ operation }: { operation: PrinterOperation }) {
	const running = operation.status === "running";
	const now = useNow(running);
	const timeline = operationTimeline(operation.operationId);
	const { bars, span, timed } = waterfallBars(operation, timeline, now);
	const barByStep = new Map(bars.map((b) => [b.step, b]));
	const groups = operationStepGroups(operation);
	const ordered = groups.flatMap((g) => g.steps);
	const variant = OPERATION_STATUS_VARIANT[operation.status] ?? "info";
	const failure =
		operation.status === "failed"
			? describeCause(operation.cause, operation.errorDetail, operation.kind)
			: null;
	// Szczegół z backendu dopisujemy pod podpowiedzią, chyba że już ją zastąpił (nieznana przyczyna).
	const showErrorDetail =
		failure != null && operation.errorDetail != null && failure.hint !== operation.errorDetail;
	const single = singleDeviceOperation(operation.kind);
	const singleDevice = single ? singleDeviceName(operation) : null;
	let waitingIndex = 0;

	return (
		<div
			className="space-y-3"
			data-testid="operation-progress"
			data-status={operation.status}
			data-timed={timed ? "true" : "false"}
		>
			<div className="flex items-start justify-between gap-2">
				<div className="min-w-0 text-sm">
					<p className="truncate font-medium">
						{operationKindLabel(operation.kind)}
						{operation.printerName ? `: ${operation.printerName}` : ""}
					</p>
					<p className="truncate text-xs text-muted-foreground">
						{single ? (singleDevice ?? "—") : (operation.hostDisplayName ?? "—")}
						{!single && operation.clientDisplayName ? ` → ${operation.clientDisplayName}` : ""}
						{" · "}
						{formatDateTime(operation.createdAt)}
						{operation.actorName ? ` · ${operation.actorName}` : ""}
					</p>
				</div>
				<div className="flex shrink-0 items-center gap-2">
					<span className="font-mono text-[11px] tabular-nums text-muted-foreground">
						{formatTotalDuration(operationDuration(operation, now))}
					</span>
					<span className={cn(badgeBase, statusClasses[variant])}>
						{OPERATION_STATUS_LABELS[operation.status] ?? operation.status}
					</span>
				</div>
			</div>

			<div className="space-y-2">
				{groups.map((group) => (
					<div key={group.title}>
						<p className="mb-0.5 truncate text-[11px] font-medium text-muted-foreground">
							{group.title}
						</p>
						<ul>
							{group.steps.map((step) => {
								const bar = barByStep.get(step.step) ?? {
									step: step.step,
									status: step.status,
									from: null,
									to: null,
									duration: null,
									sameAck: false,
								};
								const geometry = barGeometry(
									bar,
									span,
									timed,
									ordered.indexOf(step),
									ordered.length,
									bar.to == null && step.status !== "running" ? waitingIndex++ : 0,
								);
								return <StepRow key={step.step} step={step} bar={bar} geometry={geometry} />;
							})}
						</ul>
					</div>
				))}
				{operation.steps.length === 0 && running ? (
					<p className="flex items-center gap-2 text-sm text-muted-foreground">
						<Loader2 className="size-4 motion-safe:animate-spin" aria-hidden />
						Wysyłanie polecenia do komputera…
					</p>
				) : null}
				{!timed && operation.steps.length > 0 ? (
					<p className="text-[11px] text-muted-foreground">
						Czasy kroków panel zna tylko dla operacji obserwowanych na żywo — tu widać kolejność i
						czas całej operacji.
					</p>
				) : null}
			</div>

			{failure ? (
				<div role="alert" className={cn("rounded-md border p-3 text-sm", statusClasses.error)}>
					<p className="flex items-start gap-2 font-medium">
						<TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
						{failure.title}
					</p>
					<p className="mt-1 pl-6">{failure.hint}</p>
					{showErrorDetail ? <p className="mt-1 pl-6 text-xs">{operation.errorDetail}</p> : null}
				</div>
			) : null}

			{operation.status === "succeeded" ? (
				<p role="status" className={cn("rounded-md border p-3 text-sm", statusClasses.success)}>
					{successMessage(operation)}
				</p>
			) : null}
			{/* Ostrzeżenia udanych kroków (np. połączenie po IP) — dyskretnie, nie jako błąd. */}
			{operation.status === "succeeded"
				? operationWarnings(operation.steps).map((text) => (
						<p key={text} className="text-xs text-muted-foreground">
							{text}
						</p>
					))
				: null}

			<TechnicalDetails operation={operation} />
			{operation.finishedAt ? (
				<p className="text-[11px] text-muted-foreground">
					Zakończono: {formatDateTime(operation.finishedAt)}
				</p>
			) : null}
		</div>
	);
}
